import { Box, InputAdornment, Slider, Stack, TextField } from "@mui/material"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useClearRefinements, useRange } from "react-instantsearch-core"

import FilterSection from "@/section/items/components/FilterSection"
import { useAuthStore } from "@/store/auth"
import { sendFilterChangedEvent } from "@/utils/posthog"

function getPriceAttribute(currencyCode?: string) {
  return currencyCode === "NOK" ? "min_price_nok" : "min_price"
}

function formatPrice(value: number, currencyCode?: string) {
  return currencyCode === "NOK" ? `kr ${Math.round(value)}` : `€${Math.round(value)}`
}

function getCurrencyAdornment(currencyCode?: string) {
  return currencyCode === "NOK" ? "kr" : "€"
}

function normalizeRange(
  nextValue: [number, number],
  rangeMin: number,
  rangeMax: number
): [number, number] {
  const nextMin = Math.min(Math.max(nextValue[0], rangeMin), rangeMax)
  const nextMax = Math.min(Math.max(nextValue[1], rangeMin), rangeMax)

  return nextMin <= nextMax ? [nextMin, nextMax] : [nextMax, nextMin]
}

function parsePriceInput(input: string, fallback: number, rangeMin: number, rangeMax: number) {
  const trimmed = input.trim()
  if (trimmed === "") {
    return fallback
  }

  const parsed = Number(trimmed)
  if (Number.isNaN(parsed)) {
    return fallback
  }

  return Math.min(Math.max(parsed, rangeMin), rangeMax)
}

export default function PriceRangeFilter() {
  const { t } = useTranslation("items")
  const { user } = useAuthStore((state) => state)
  const currencyCode = user?.selected_currency_code
  const prevCurrencyRef = useRef(currencyCode)

  const { refine: clearOtherPrice } = useClearRefinements({
    includedAttributes: ["min_price", "min_price_nok"],
  })

  useEffect(() => {
    if (prevCurrencyRef.current !== currencyCode) {
      clearOtherPrice()
      prevCurrencyRef.current = currencyCode
    }
  }, [currencyCode, clearOtherPrice])

  const { range, start, refine, canRefine } = useRange({
    attribute: getPriceAttribute(currencyCode),
  })

  const min = range.min ?? 0
  const max = range.max ?? 0

  const currentMin = start[0] !== -Infinity && start[0] != null ? start[0] : min
  const currentMax = start[1] !== Infinity && start[1] != null ? start[1] : max

  const [value, setValue] = useState<[number, number]>([currentMin, currentMax])
  const [minInput, setMinInput] = useState(String(currentMin))
  const [maxInput, setMaxInput] = useState(String(currentMax))
  const isMinFocusedRef = useRef(false)
  const isMaxFocusedRef = useRef(false)
  const isActive = currentMin > min || currentMax < max

  useEffect(() => {
    setValue([currentMin, currentMax])
  }, [currentMin, currentMax])

  useEffect(() => {
    if (!isMinFocusedRef.current) {
      setMinInput(String(value[0]))
    }
  }, [value[0]])

  useEffect(() => {
    if (!isMaxFocusedRef.current) {
      setMaxInput(String(value[1]))
    }
  }, [value[1]])

  const applyValue = useCallback(
    (nextValue: [number, number]) => {
      const normalizedValue = normalizeRange(nextValue, min, max)
      const nextIsActive = normalizedValue[0] > min || normalizedValue[1] < max
      const hasChanged = normalizedValue[0] !== currentMin || normalizedValue[1] !== currentMax

      setValue(normalizedValue)

      if (hasChanged) {
        sendFilterChangedEvent({
          filterGroup: "price",
          filterKey: getPriceAttribute(currencyCode),
          filterLabel: t("Price"),
          isActive: nextIsActive,
          value: `${normalizedValue[0]}-${normalizedValue[1]}`,
          metadata: {
            currency_code: currencyCode,
            selected_min: normalizedValue[0],
            selected_max: normalizedValue[1],
            available_min: min,
            available_max: max,
          },
        })
      }

      refine(normalizedValue)
    },
    [currencyCode, currentMax, currentMin, max, min, refine, t]
  )

  const handleSliderChange = useCallback((_: unknown, newValue: number | number[]) => {
    setValue(newValue as [number, number])
  }, [])

  const handleSliderCommit = useCallback(
    (_: unknown, newValue: number | number[]) => {
      applyValue(newValue as [number, number])
    },
    [applyValue]
  )

  const commitMinInput = useCallback(() => {
    const nextMin = parsePriceInput(minInput, value[0], min, max)
    const normalizedValue = normalizeRange([nextMin, value[1]], min, max)

    setMinInput(String(normalizedValue[0]))
    applyValue(normalizedValue)
  }, [applyValue, max, min, minInput, value])

  const commitMaxInput = useCallback(() => {
    const nextMax = parsePriceInput(maxInput, value[1], min, max)
    const normalizedValue = normalizeRange([value[0], nextMax], min, max)

    setMaxInput(String(normalizedValue[1]))
    applyValue(normalizedValue)
  }, [applyValue, max, maxInput, min, value])

  const handleMinFocus = useCallback(() => {
    isMinFocusedRef.current = true
  }, [])

  const handleMaxFocus = useCallback(() => {
    isMaxFocusedRef.current = true
  }, [])

  const handleMinBlur = useCallback(() => {
    isMinFocusedRef.current = false
    commitMinInput()
  }, [commitMinInput])

  const handleMaxBlur = useCallback(() => {
    isMaxFocusedRef.current = false
    commitMaxInput()
  }, [commitMaxInput])

  const handleInputKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") {
      return
    }

    event.preventDefault()
    ;(event.target as HTMLInputElement).blur()
  }, [])

  if (!canRefine || min === max) {
    return null
  }

  return (
    <FilterSection title={t("Price")} activeCount={isActive ? 1 : 0} trackingKey="price">
      <Box sx={{ paddingX: 1 }}>
        <Slider
          value={value}
          onChange={handleSliderChange}
          onChangeCommitted={handleSliderCommit}
          min={min}
          max={max}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => formatPrice(v, currencyCode)}
        />
      </Box>
      <Stack direction="row" spacing={2} sx={{ justifyContent: "space-between" }}>
        <TextField
          size="small"
          label={t("MinLabel")}
          type="number"
          variant="standard"
          value={minInput}
          onChange={(e) => setMinInput(e.target.value)}
          onFocus={handleMinFocus}
          onBlur={handleMinBlur}
          slotProps={{
            htmlInput: {
              onKeyDown: handleInputKeyDown,
            },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  {getCurrencyAdornment(currencyCode)}
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField
          size="small"
          label={t("MaxLabel")}
          type="number"
          variant="standard"
          value={maxInput}
          onChange={(e) => setMaxInput(e.target.value)}
          onFocus={handleMaxFocus}
          onBlur={handleMaxBlur}
          slotProps={{
            htmlInput: {
              onKeyDown: handleInputKeyDown,
            },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  {getCurrencyAdornment(currencyCode)}
                </InputAdornment>
              ),
            },
          }}
        />
      </Stack>
    </FilterSection>
  )
}
